/**
 * dashboardLayout.js - the customizable Dashboard's layout model (10/12/26,
 * Sharif: "move stuff around and make it as big or small as they want and it
 * fits all"). PURE functions only; DashboardGrid draws what they return.
 *
 * A layout is an ORDERED list of widgets with a size, nothing else:
 *
 *   { v: 1, items: [{ id, w, h }], hidden: [id] }
 *
 *   w  width in columns of a 12-column grid
 *   h  height in rows of ROW_PX (a KPI box is 2 rows)
 *
 * The grid is filled in that order, left to right and top to bottom: each
 * widget takes the first free cell at or after the one before it (flow), so
 * nothing ever overlaps and a widget may sit beside a taller one (a column of
 * lists next to a table). Moving a widget = moving it in the order; resizing
 * = changing w / h; the rest reflows around it. An empty cell is a SPACER
 * ("Empty space" in the tray): a widget of its own that draws nothing outside
 * edit mode, so a gap is something the person placed and can see, never an
 * accident of dragging (Shawn: two empty boxes at the top).
 *
 * On a narrower screen the same order flows into fewer columns (fitToColumns):
 * widths scale down, spacers drop out (a gap meant for the wide grid means
 * nothing in a phone's single column).
 *
 * server/routes/dashboardLayout.js keeps the same widget ids and size limits
 * (server/test_dashboard_layout.js compares them line by line).
 */

export const COLS = 12;
export const ROW_PX = 64;
export const GAP_PX = 16;
/** Widths of the grid area (px) at which it drops to 6 and to 1 column. */
export const WIDE_PX = 880;
export const MEDIUM_PX = 560;
export const MAX_ITEMS = 60;
export const MAX_SPACERS = 24;

/* Every widget the Dashboard has, in the default order, at its default size
   (today's look: the six KPI boxes three to a row, the three to-do lists in
   a row, Active Projects then Utilization full width), and how small / big it
   may go. Keep one widget per line: the server test reads these lines. */
export const WIDGETS = [
  { id: 'recent', title: 'Recently viewed', w: 12, h: 1, minW: 3, maxW: 12, minH: 1, maxH: 3 },
  { id: 'kpi-pipeline', title: 'Total Pipeline', w: 4, h: 2, minW: 2, maxW: 6, minH: 2, maxH: 4 },
  { id: 'kpi-open', title: 'Open Opportunities', w: 4, h: 2, minW: 2, maxW: 6, minH: 2, maxH: 4 },
  { id: 'week-quoted', title: 'Quoted (week)', w: 4, h: 2, minW: 2, maxW: 6, minH: 2, maxH: 4 },
  { id: 'week-pos', title: "PO's (week)", w: 4, h: 2, minW: 2, maxW: 6, minH: 2, maxH: 4 },
  { id: 'week-collected', title: '$ collected (week)', w: 4, h: 2, minW: 2, maxW: 6, minH: 2, maxH: 4 },
  { id: 'week-invoices', title: 'Invoices (week)', w: 4, h: 2, minW: 2, maxW: 6, minH: 2, maxH: 4 },
  { id: 'my-tasks', title: 'My tasks', w: 4, h: 5, minW: 3, maxW: 12, minH: 3, maxH: 14 },
  { id: 'no-next-step', title: 'No next step', w: 4, h: 5, minW: 3, maxW: 12, minH: 3, maxH: 14 },
  { id: 'project-tasks', title: 'Project tasks', w: 4, h: 5, minW: 3, maxW: 12, minH: 3, maxH: 14 },
  { id: 'active-projects', title: 'Active Projects', w: 12, h: 8, minW: 4, maxW: 12, minH: 4, maxH: 16 },
  { id: 'utilization', title: 'Utilization', w: 12, h: 5, minW: 4, maxW: 12, minH: 3, maxH: 10 },
];
export const SPACER = { title: 'Empty space', w: 3, h: 2, minW: 1, maxW: 12, minH: 1, maxH: 12 };

const BY_ID = Object.fromEntries(WIDGETS.map((w) => [w.id, w]));

export const isSpacer = (id) => typeof id === 'string' && /^spacer-[1-9]\d{0,2}$/.test(id);
export const isKnown = (id) => isSpacer(id) || Object.prototype.hasOwnProperty.call(BY_ID, id);
/** The widget's catalog entry (a spacer's shared one), or null. */
export const widgetOf = (id) => (isSpacer(id) ? SPACER : BY_ID[id] || null);
export const titleOf = (id) => (widgetOf(id) || {}).title || id;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const whole = (v, d) => (v !== null && v !== '' && Number.isFinite(Number(v)) ? Math.round(Number(v)) : d);

/** One item at a size its widget allows ({ id, w, h }). */
export function clampItem(item) {
  const L = widgetOf(item.id);
  return { id: item.id, w: clamp(whole(item.w, L.w), L.minW, L.maxW), h: clamp(whole(item.h, L.h), L.minH, L.maxH) };
}

/** Today's Dashboard. */
export function defaultLayout() {
  return { v: 1, items: WIDGETS.map(({ id, w, h }) => ({ id, w, h })), hidden: [] };
}

/**
 * A saved layout made safe to draw: unknown widgets (removed since it was
 * saved) dropped, sizes clamped, duplicates gone, at most MAX_SPACERS
 * spacers; a widget added to the Dashboard since then (neither placed nor
 * hidden) joins at the end at its default size. Anything unreadable is the
 * default layout.
 */
export function normalizeLayout(saved) {
  if (!saved || typeof saved !== 'object' || !Array.isArray(saved.items)) return defaultLayout();
  const seen = new Set();
  const items = [];
  let spacers = 0;
  for (const it of saved.items.slice(0, MAX_ITEMS * 2)) {
    if (!it || typeof it !== 'object' || !isKnown(it.id) || seen.has(it.id)) continue;
    if (isSpacer(it.id) && (spacers += 1) > MAX_SPACERS) continue;
    seen.add(it.id);
    items.push(clampItem(it));
  }
  const hidden = [...new Set((Array.isArray(saved.hidden) ? saved.hidden : []).filter((id) => BY_ID[id] && !seen.has(id)))];
  for (const { id, w, h } of WIDGETS) if (!seen.has(id) && !hidden.includes(id)) items.push({ id, w, h });
  return { v: 1, items: items.slice(0, MAX_ITEMS), hidden };
}

/** Same widgets, same order, same sizes, same hidden list? */
export function sameLayout(a, b) {
  const key = (l) => JSON.stringify([l.items.map((i) => [i.id, i.w, i.h]), [...l.hidden].sort()]);
  return key(a) === key(b);
}

/* ── where things land ──────────────────────────────────────────────── */

const free = (placed, x, y, w, h) => !placed.some((p) => x < p.x + p.w && p.x < x + w && y < p.y + p.h && p.y < y + h);

/**
 * Each item's cell { id, x, y, w, h } on a grid of `cols` columns, in order:
 * the first free spot at or after the previous item's (reading order), so
 * the order is kept and an item can fill the space beside or under a
 * taller one. Never overlapping.
 */
export function flow(items, cols = COLS) {
  const placed = [];
  let cy = 0;
  let cx = 0;
  for (const it of items) {
    const w = clamp(it.w, 1, cols);
    let y = cy;
    let x = cx;
    for (;;) {
      if (x + w > cols) {
        y += 1;
        x = 0;
        continue;
      }
      if (free(placed, x, y, w, it.h)) break;
      x += 1;
    }
    placed.push({ id: it.id, x, y, w, h: it.h });
    cy = y;
    cx = x + w;
  }
  return placed;
}

/** Rows the flowed items take. */
export const rowsOf = (cells) => cells.reduce((m, c) => Math.max(m, c.y + c.h), 0);

/** 12, 6 or 1 columns for a grid area this wide (px); unknown (0) is wide. */
export function columnsFor(width) {
  if (!width || width >= WIDE_PX) return COLS;
  return width >= MEDIUM_PX ? 6 : 1;
}

/**
 * The person's order on a narrower grid: widths scale with the columns (a
 * widget wider than half of them takes the row), heights stay, spacers drop
 * out. 12 columns is the layout itself.
 */
export function fitToColumns(items, cols) {
  if (cols >= COLS) return items;
  return items
    .filter((it) => !isSpacer(it.id))
    .map((it) => {
      const w = clamp(Math.round((it.w * cols) / COLS), 1, cols);
      return { ...it, w: w > cols / 2 ? cols : w };
    });
}

/* ── edits (each returns a new item list) ───────────────────────────── */

/** `id` moved to position `to` in the list without it. */
export function moveTo(items, id, to) {
  const it = items.find((i) => i.id === id);
  if (!it) return items;
  const rest = items.filter((i) => i.id !== id);
  rest.splice(clamp(to, 0, rest.length), 0, it);
  return rest;
}

/** `id` at a new size (clamped to what the widget allows). */
export function resizeItem(items, id, w, h) {
  return items.map((i) => (i.id === id ? clampItem({ id, w, h }) : i));
}

/** A new spacer's id: the lowest number not used. */
export function nextSpacerId(items) {
  const used = new Set(items.map((i) => i.id));
  let n = 1;
  while (used.has(`spacer-${n}`)) n += 1;
  return `spacer-${n}`;
}

/** Hide a widget (a spacer is simply removed). */
export function hideWidget(layout, id) {
  const items = layout.items.filter((i) => i.id !== id);
  const hidden = isSpacer(id) || layout.hidden.includes(id) ? layout.hidden : [...layout.hidden, id];
  return { ...layout, items, hidden };
}

/** Show a hidden widget (or add a spacer, id 'spacer'), at the end, at its default size. */
export function showWidget(layout, id) {
  if (id === 'spacer') {
    if (layout.items.filter((i) => isSpacer(i.id)).length >= MAX_SPACERS) return layout;
    return { ...layout, items: [...layout.items, { id: nextSpacerId(layout.items), w: SPACER.w, h: SPACER.h }] };
  }
  const W = BY_ID[id];
  if (!W || layout.items.some((i) => i.id === id)) return layout;
  return { ...layout, items: [...layout.items, { id, w: W.w, h: W.h }], hidden: layout.hidden.filter((h) => h !== id) };
}

/**
 * Put an edited copy of the SHOWN items back into the full list: widgets the
 * person cannot see (access) are not on screen while they edit, so they keep
 * their own places in the order, and the shown ones fill the other places in
 * their new order. Shown items no longer in `edited` were hidden; new ones
 * join at the end.
 */
export function mergeShown(full, shown, edited) {
  const editedIds = new Set(edited.map((i) => i.id));
  const queue = [...edited];
  const out = [];
  for (const it of full) {
    if (!shown(it.id)) out.push(it);
    else if (editedIds.has(it.id) && queue.length) out.push(queue.shift());
  }
  return out.concat(queue);
}

/* ── pointer → grid ─────────────────────────────────────────────────── */

/** Column width (px) of a grid `width` px wide. */
export const colWidth = (width, cols = COLS) => (width - GAP_PX * (cols - 1)) / cols;

/** The cell (fractional) under a point (px, relative to the grid's top left). */
export function cellAt(px, py, width, cols = COLS) {
  return { cx: px / (colWidth(width, cols) + GAP_PX), cy: py / (ROW_PX + GAP_PX) };
}

/**
 * Where a dragged item lands in the order when the pointer is at cell
 * (cx, cy) (fractional), given where the OTHER items are drawn: over an item,
 * before it (its left half) or after it (its right half); in a gap, after
 * every item that starts before that spot in reading order.
 */
export function dropIndex(otherCells, cx, cy) {
  const over = otherCells.findIndex((c) => cx >= c.x && cx < c.x + c.w && cy >= c.y && cy < c.y + c.h);
  if (over >= 0) {
    const c = otherCells[over];
    return cx < c.x + c.w / 2 ? over : over + 1;
  }
  const row = Math.floor(cy);
  const col = Math.floor(cx);
  return otherCells.filter((c) => c.y < row || (c.y === row && c.x < col)).length;
}

/** Size (cells) for a resize handle dragged to (px, py) from a widget whose top left is (left, top) px. */
export function sizeAt(px, py, left, top, width, cols = COLS) {
  return {
    w: Math.max(1, Math.round((px - left + GAP_PX) / (colWidth(width, cols) + GAP_PX))),
    h: Math.max(1, Math.round((py - top + GAP_PX) / (ROW_PX + GAP_PX))),
  };
}
